import style from "./ChoosePlan.module.css";

import Icon from "@/ui/icon/Icon";
import Button from "@/ui/button/Button";

import { Navigate, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { successToast } from "@/ui/toast/Toast";
import { useSubscribe } from "@/features/payments/hooks/useSubscribe";
import Spinner from "@/features/forms/components/Spinner";
import { useUser } from "../../hooks/useUser";
import { usePriceInfo } from "@/features/payments/hooks/usePriceInfo";
import { derivePlans } from "@/features/payments/plans";
import { useEffect, useState } from "react";
import { formatPriceAmount, formatPrice } from "@/i18n/format";
import { useCurrentLanguage } from "@/i18n/useLocalized";

function ChoosePlan({ onRegister = false }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const language = useCurrentLanguage();
	const { subscribe, isPending } = useSubscribe();
	const { priceInfo, isPending: isPriceInfoPending } = usePriceInfo();
	const { user, isLoggedIn, isPending: isPendingUser } = useUser();

	const validPlans = ["WK", "MO", "QU", "YR"];
	const defaultPlan =
		user?.payment_plan && validPlans.includes(user?.payment_plan)
			? user?.payment_plan
			: "MO";

	const [selectedPlanId, setSelectedPlanId] = useState(defaultPlan);

	useEffect(() => {
		const shouldSkipPayment =
			sessionStorage.getItem("skipPayment") === "true";
		const selectedPlanId = sessionStorage.getItem("selectedPlanId");

		// Exit early if conditions aren't met
		if (!shouldSkipPayment || !user || user?.is_subscribed) return;

		const handleSubscription = async () => {
			sessionStorage.removeItem("skipPayment");
			sessionStorage.removeItem("selectedPlanId");

			if (selectedPlanId !== "free") {
				await subscribe(selectedPlanId);
			} else {
				successToast(i18n.t("payments:choose.completed"));
				navigate("/", { replace: true });
			}
		};

		handleSubscription();
	}, [user]);

	// if (user?.is_subscribed) {
	// 	return <Navigate to="/" replace={true} />;
	// }

	// TODO: add spinner
	if (isPriceInfoPending) return <></>;

	// One catalogue, shared with the plan picker in settings, priced from
	// /payments/price-info/ so the page cannot quote an amount the checkout
	// will not charge.
	const plans = derivePlans(priceInfo).map((plan) => ({
		...plan,
		isLoading: isPending,
		onSelect: async () => {
			if (user) {
				await subscribe(plan.id);
			} else {
				// Finish registration first, then come back and buy this plan.
				sessionStorage.setItem("skipPayment", "true");
				sessionStorage.setItem("selectedPlanId", plan.id);
				navigate("/register");
			}
		},
	}));

	const benefits = [
		{
			name: t("payments:choose.benefits.access"),
		},
		{
			name: t("auth:upgrade.benefits.certificate"),
		},
		{
			name: t("auth:upgrade.benefits.ai"),
		},
		{
			name: t("payments:choose.benefits.quizzes"),
		},
		{
			name: t("payments:choose.benefits.paths"),
		},
	];

	const selectedPlan = plans.find((plan) => plan.id == selectedPlanId);

	const currentPlan = user?.payment_plan;

	// English writes the sign first: €14.99. Bosnian keeps it after: 14,9 €.
	const isEnglish = language === "en";
	const currency = <div className={style.currency}>€</div>;

	// Bosnian has always written these two with the sign glued on ("30,00€")
	// and spaced ("15,00 €"), so its keys take the bare number. English takes
	// the finished price, "€30.00", from the same formatter as the billing page.
	const euros = (value) =>
		isEnglish ? formatPrice(value, "EUR", language) : value;

	return (
		<div className={`${style.content} ${onRegister && style.register}`}>
			<div className={style.header}>
				<Icon icon="fa-tasks" />
				<h2>{t("payments:choose.title")}</h2>
			</div>
			<div className={style.grid}>
				<div className={style.offersHolder}>
					{plans.map((plan) => (
						<div
							className={`${style.offer} ${
								plan.preferred && style.preferred
							} ${selectedPlanId === plan.id && style.active} ${
								currentPlan === plan.id && style.current
							}`}
							key={plan.id}
							onClick={() => setSelectedPlanId(plan.id)}
						>
							<div className={style.offerDetails}>
								<div className={style.checkbox}>
									{selectedPlanId === plan.id && (
										<Icon icon="fa-check" />
									)}
								</div>
								<div className={style.offerInfo}>
									<h3>{t(plan.nameKey)}</h3>
									{currentPlan === plan.id ? (
										<div className={style.currentTag}>
											{t("payments:planCard.currentPlan")}
										</div>
									) : (
										plan.save != 0 && (
											<div className={style.saveTag}>
												{t("payments:planCard.discount", {
													percent: plan.save,
												})}
											</div>
										)
									)}
								</div>
							</div>
							<div className={style.priceHolder}>
								<div className={style.totalPrice}>
									{isEnglish && currency}
									<div className={style.amount}>
										{formatPriceAmount(plan.totalPrice, language)}
									</div>
									{!isEnglish && currency}
								</div>
								<div className={style.pricePerDay}>
									{isEnglish && currency}
									<div className={style.amount}>
										{formatPriceAmount(plan.pricePerDay, language)}
									</div>
									{!isEnglish && currency}
									<div className={style.sep}>
										<Icon icon="fa-slash-forward" />
									</div>
									<div className={style.period}>{t("payments:planCard.perDay")}</div>
								</div>
							</div>
						</div>
					))}
				</div>
				<div className={style.benefits}>
					{selectedPlan?.preferred && (
						<div className={style.mostPopular}>
							<span>{t("payments:choose.mostPopular")}</span>
							<Icon icon="fa-fire" />
						</div>
					)}
					<div className={style.benefitsHeader}>
						<h3>{t(selectedPlan.nameKey)}</h3>
					</div>
					<div className={style.benefitsBody}>
						<ul>
							{benefits.map((benefit, index) => (
								<li key={index}>
									<Icon icon="fa-check" />
									<span>{benefit.name}</span>
								</li>
							))}
						</ul>
					</div>
					<div className={style.benefitsFooter}>
						{selectedPlanId !== "WK" && (
							<div className={style.benefitsColumn}>
								<span>{t("payments:choose.originalPrice")}</span>
								<div className={style.originalPrice}>
									{t("payments:choose.originalAmount", {
										price: euros(selectedPlan.duration),
									})}
								</div>
								<div className={style.savings}>
									<span>{t("payments:choose.savings")}</span>
									<span className={style.savingsAmount}>
										{t("payments:choose.savingsAmount", {
											price: euros(
												Math.floor(
													selectedPlan.duration -
														selectedPlan.totalPrice
												)
											),
										})}
									</span>
								</div>
							</div>
						)}

						<div className={style.benefitsColumn}>
							<span>{t("payments:choose.total")}</span>
							<div
								className={`${style.priceHolder} ${
									selectedPlanId == "WK" ? style.onePrice : ""
								}`}
							>
								<div className={style.totalPrice}>
									{isEnglish && currency}
									<div className={style.amount}>
										{formatPriceAmount(selectedPlan.totalPrice, language)}
									</div>
									{!isEnglish && currency}
								</div>
								<div className={style.pricePerDay}>
									{isEnglish && currency}
									<div className={style.amount}>
										{formatPriceAmount(selectedPlan.pricePerDay, language)}
									</div>
									{!isEnglish && currency}
									<div className={style.sep}>
										<Icon icon="fa-slash-forward" />
									</div>
									<div className={style.period}>{t("payments:planCard.perDay")}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className={style.footer}>
				<div className={style.btnsHolder}>
					<Button
						onClick={selectedPlan.onSelect}
						className={style.btn}
					>
						{selectedPlan.isLoading ? (
							<Spinner />
						) : (
							t("payments:choose.buy")
						)}
						<Icon iconType="fa-solid" icon="fa-long-arrow-right" />
					</Button>
					<Button to={isLoggedIn ? "/" : "/register"}>
						{t("payments:plans.free")}
					</Button>
				</div>
				<div className={style.security}>
					<Icon icon="fa-shield-check" />
					<div className={style.column}>
						<span>{t("payments:choose.secure")}</span>
						<span>{t("payments:choose.moneyBack")}</span>
					</div>
				</div>
			</div>
		</div>
	);
}

export default ChoosePlan;
